import React, { useState, useEffect, useCallback } from 'react';
import { Crown, Star, Zap, Check, ArrowRight, Shield, Trophy, Sparkles } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function PremiumPage() {
  const [tiers, setTiers] = useState({});
  const [currentTier, setCurrentTier] = useState(null);
  const [usage, setUsage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [upgrading, setUpgrading] = useState(false);
  const [billingCycle, setBillingCycle] = useState('monthly');

  const token = localStorage.getItem('token');

  const fetchData = useCallback(async () => {
    try {
      const [tiersRes, myTierRes, usageRes] = await Promise.all([
        fetch(`${API_URL}/api/premium/tiers`),
        fetch(`${API_URL}/api/premium/my-tier`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch(`${API_URL}/api/premium/usage`, {
          headers: { 'Authorization': `Bearer ${token}` }
        })
      ]);

      if (tiersRes.ok) {
        const data = await tiersRes.json();
        setTiers(data.tiers || {});
      }
      if (myTierRes.ok) setCurrentTier(await myTierRes.json());
      if (usageRes.ok) setUsage(await usageRes.json());
    } catch (err) {
      console.error('Error:', err);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleUpgrade = async (tier) => {
    setUpgrading(true);
    try {
      // Use PayPal Premium API for real payments
      const res = await fetch(`${API_URL}/api/paypal-premium/create-order`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ tier, billing_cycle: billingCycle, origin: window.location.origin })
      });

      if (res.ok) {
        const data = await res.json();
        
        // If PayPal approval URL exists, redirect to PayPal
        if (data.approval_url) {
          // Store order ID for return handling
          localStorage.setItem('pending_order_id', data.order_id);
          // Redirect to PayPal checkout
          window.location.href = data.approval_url;
        } else if (data.demo_mode) {
          // Demo mode - auto activate
          const activateRes = await fetch(`${API_URL}/api/paypal-premium/capture-order`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`
            },
            body: JSON.stringify({ order_id: data.order_id })
          });
          if (activateRes.ok) {
            const result = await activateRes.json();
            alert(`${result.message} 🎉`);
            fetchData();
          }
        }
      } else {
        const err = await res.json();
        alert(err.detail || 'Fehler bei der Zahlung');
      }
    } catch (err) {
      alert('Netzwerkfehler');
    } finally {
      setUpgrading(false);
    }
  };

  // Handle PayPal return
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const success = params.get('success');
    const orderId = params.get('order') || localStorage.getItem('pending_order_id');
    
    if (success === 'true' && orderId && token) {
      // Capture the order after PayPal approval
      fetch(`${API_URL}/api/paypal-premium/capture-order`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ order_id: orderId })
      })
      .then(res => res.json())
      .then(data => {
        if (data.tier) {
          alert(`Premium ${data.tier} erfolgreich aktiviert! 🎉`);
          localStorage.removeItem('pending_order_id');
          // Clean URL
          window.history.replaceState({}, '', '/premium');
          fetchData();
        }
      })
      .catch(err => console.error('Capture error:', err));
    }
  }, [token, fetchData]);

  const tierOrder = ['free', 'basic', 'pro', 'elite'];
  const tierColors = {
    free: 'from-gray-600 to-gray-700',
    basic: 'from-blue-600 to-blue-700',
    pro: 'from-purple-600 to-purple-700',
    elite: 'from-yellow-500 to-yellow-600',
  };

  const tierIcons = {
    free: <Star className="w-8 h-8" />,
    basic: <Shield className="w-8 h-8" />,
    pro: <Zap className="w-8 h-8" />,
    elite: <Crown className="w-8 h-8" />,
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-950 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-teal-400"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-950 text-white p-4 md:p-8" data-testid="premium-page">
      {/* Header */}
      <div className="max-w-6xl mx-auto mb-8 text-center">
        <div className="flex items-center justify-center gap-3 mb-4">
          <Sparkles className="w-8 h-8 text-yellow-400" />
          <h1 className="text-3xl font-bold">Premium Upgrades</h1>
        </div>
        <p className="text-gray-400">Schalte mehr Features frei und maximiere deine Gewinne</p>
      </div>

      {/* Billing Toggle */}
      <div className="max-w-6xl mx-auto mb-8 flex justify-center">
        <div className="bg-gray-800 rounded-lg p-1 flex gap-1">
          <button
            onClick={() => setBillingCycle('monthly')}
            className={`px-4 py-2 rounded-md transition ${
              billingCycle === 'monthly' ? 'bg-teal-500 text-white' : 'text-gray-400'
            }`}
          >
            Monatlich
          </button>
          <button
            onClick={() => setBillingCycle('yearly')}
            className={`px-4 py-2 rounded-md transition ${
              billingCycle === 'yearly' ? 'bg-teal-500 text-white' : 'text-gray-400'
            }`}
          >
            Jaehrlich <span className="text-xs text-green-400">-17%</span>
          </button>
        </div>
      </div>

      {/* Current Usage */}
      {usage && (
        <div className="max-w-6xl mx-auto mb-8">
          <div className="bg-gray-900 rounded-xl p-6 border border-gray-800">
            <h3 className="font-semibold mb-4 flex items-center gap-2">
              <Trophy className="w-5 h-5 text-teal-400" />
              Dein aktueller Status: <span className="text-teal-400">{tiers[usage.tier]?.name || 'Free'}</span>
            </h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="bg-gray-800 rounded-lg p-3 text-center">
                <div className="text-lg font-bold">{usage.tips_today} / {usage.tips_limit}</div>
                <div className="text-xs text-gray-400">Tipps heute</div>
              </div>
              <div className="bg-gray-800 rounded-lg p-3 text-center">
                <div className="text-lg font-bold">{usage.alerts_active} / {usage.alerts_limit}</div>
                <div className="text-xs text-gray-400">Alerts aktiv</div>
              </div>
              <div className="bg-gray-800 rounded-lg p-3 text-center">
                <div className={`text-lg font-bold ${usage.can_use_ai_council ? 'text-green-400' : 'text-red-400'}`}>
                  {usage.can_use_ai_council ? 'Ja' : 'Nein'}
                </div>
                <div className="text-xs text-gray-400">AI Council</div>
              </div>
              <div className="bg-gray-800 rounded-lg p-3 text-center">
                <div className={`text-lg font-bold ${usage.can_use_precision_mode ? 'text-green-400' : 'text-red-400'}`}>
                  {usage.can_use_precision_mode ? 'Ja' : 'Nein'}
                </div>
                <div className="text-xs text-gray-400">Precision Mode</div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Tier Cards */}
      <div className="max-w-6xl mx-auto grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {tierOrder.map(tierKey => {
          const tier = tiers[tierKey];
          if (!tier) return null;
          
          const isCurrent = currentTier?.current_tier === tierKey;
          const price = billingCycle === 'yearly' ? tier.price_yearly : tier.price_monthly;
          
          return (
            <div
              key={tierKey}
              className={`relative rounded-xl overflow-hidden ${
                isCurrent ? 'ring-2 ring-teal-400' : ''
              }`}
            >
              {/* Header */}
              <div className={`bg-gradient-to-br ${tierColors[tierKey]} p-6 text-center`}>
                <div className="flex justify-center mb-2">{tierIcons[tierKey]}</div>
                <h3 className="text-xl font-bold">{tier.name}</h3>
                <div className="text-3xl font-bold mt-2">
                  {price === 0 ? 'Gratis' : `€${price}`}
                  {price > 0 && <span className="text-sm font-normal">/{billingCycle === 'yearly' ? 'Jahr' : 'Monat'}</span>}
                </div>
              </div>

              {/* Features */}
              <div className="bg-gray-900 p-6 border-x border-b border-gray-800">
                <ul className="space-y-3 mb-6">
                  <li className="flex items-center gap-2 text-sm">
                    <Check className="w-4 h-4 text-green-400 flex-shrink-0" />
                    <span>{tier.tips_per_day === -1 ? 'Unbegrenzte' : tier.tips_per_day} Tipps/Tag</span>
                  </li>
                  <li className="flex items-center gap-2 text-sm">
                    <Check className="w-4 h-4 text-green-400 flex-shrink-0" />
                    <span>{tier.sports_access === 'all' ? 'Alle Sportarten' : tier.sports_access?.join(', ')}</span>
                  </li>
                  <li className="flex items-center gap-2 text-sm">
                    {tier.precision_mode ? (
                      <Check className="w-4 h-4 text-green-400 flex-shrink-0" />
                    ) : (
                      <span className="w-4 h-4 text-red-400">✕</span>
                    )}
                    <span className={!tier.precision_mode ? 'text-gray-500' : ''}>Precision Mode</span>
                  </li>
                  <li className="flex items-center gap-2 text-sm">
                    {tier.ai_council ? (
                      <Check className="w-4 h-4 text-green-400 flex-shrink-0" />
                    ) : (
                      <span className="w-4 h-4 text-red-400">✕</span>
                    )}
                    <span className={!tier.ai_council ? 'text-gray-500' : ''}>AI Council</span>
                  </li>
                  <li className="flex items-center gap-2 text-sm">
                    {tier.ad_free ? (
                      <Check className="w-4 h-4 text-green-400 flex-shrink-0" />
                    ) : (
                      <span className="w-4 h-4 text-red-400">✕</span>
                    )}
                    <span className={!tier.ad_free ? 'text-gray-500' : ''}>Werbefrei</span>
                  </li>
                  <li className="flex items-center gap-2 text-sm">
                    {tier.exclusive_tournaments ? (
                      <Check className="w-4 h-4 text-green-400 flex-shrink-0" />
                    ) : (
                      <span className="w-4 h-4 text-red-400">✕</span>
                    )}
                    <span className={!tier.exclusive_tournaments ? 'text-gray-500' : ''}>Exklusive Tournaments</span>
                  </li>
                </ul>

                {/* Action Button */}
                {isCurrent ? (
                  <button
                    disabled
                    className="w-full py-3 bg-gray-700 text-gray-400 rounded-lg font-semibold"
                  >
                    Aktuell
                  </button>
                ) : tierKey === 'free' ? (
                  <button
                    disabled
                    className="w-full py-3 bg-gray-700 text-gray-400 rounded-lg font-semibold"
                  >
                    Basisplan
                  </button>
                ) : (
                  <button
                    onClick={() => handleUpgrade(tierKey)}
                    disabled={upgrading}
                    className="w-full py-3 bg-teal-500 hover:bg-teal-400 text-white rounded-lg font-semibold transition flex items-center justify-center gap-2"
                  >
                    {upgrading ? 'Wird verarbeitet...' : (
                      <>
                        Upgrade <ArrowRight className="w-4 h-4" />
                      </>
                    )}
                  </button>
                )}
              </div>

              {/* Badge for current */}
              {isCurrent && (
                <div className="absolute top-2 right-2 bg-teal-500 text-white text-xs px-2 py-1 rounded-full">
                  Aktuell
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* FAQ */}
      <div className="max-w-4xl mx-auto mt-12">
        <h3 className="text-xl font-bold mb-4">Haeufige Fragen</h3>
        <div className="space-y-4">
          <div className="bg-gray-900 rounded-xl p-4 border border-gray-800">
            <div className="font-semibold mb-2">Kann ich jederzeit kuendigen?</div>
            <div className="text-gray-400 text-sm">Ja, du kannst dein Abo jederzeit kuendigen. Es laeuft dann bis zum Ende der Zahlungsperiode.</div>
          </div>
          <div className="bg-gray-900 rounded-xl p-4 border border-gray-800">
            <div className="font-semibold mb-2">Was ist der Precision Mode?</div>
            <div className="text-gray-400 text-sm">Der Precision Mode zeigt nur Tipps mit 85%+ Konfidenz an - unsere zuverlaessigsten Vorhersagen.</div>
          </div>
          <div className="bg-gray-900 rounded-xl p-4 border border-gray-800">
            <div className="font-semibold mb-2">Was ist AI Council?</div>
            <div className="text-gray-400 text-sm">Drei unabhaengige KI-Modelle analysieren jeden Tipp und du siehst deren Konsens.</div>
          </div>
        </div>
      </div>
    </div>
  );
}
